import IconButton from '@/components/Buttons/IconButton';
import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import CloseIcon from '@/images/CloseWithCircle.svg';
import { useAnalytics } from '@/services/analytics';
import { MixPanelEvent } from '@/utils/types';
import styled from '@emotion/styled';
import { Button, DialogContent, DialogTitle } from '@mui/material';
import { useEffect } from 'react';

const CloseButton = styled(IconButton)`
    position: absolute;
    top: 10px;
    right: 10px;
    color: ${({ theme }) => theme.colors.black[800]};
`;

const StyledDialogTitle = styled(DialogTitle)`
    &.MuiDialogTitle-root {
        color: ${({ theme }) => theme.colors.secondary[900]};
        font-size: 24px;
        font-weight: 600;
        letter-spacing: -0.48px;
        justify-content: center;
        padding-top: 16px;
        padding-bottom: 8px;
    }
`;

const StyledDialogContent = styled(DialogContent)`
    &.MuiDialogContent-root {
        display: flex;
        flex-direction: column;
        overflow: visible;
    }
`;

const Subtitle = styled.span`
    color: ${({ theme }) => theme.colors.black[700]};
    text-align: center;
    font-family: Manrope;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 20.8px;
    letter-spacing: -0.32px;
`;

const StyledButton = styled(Button)`
    font-size: 16px;
    font-weight: 600;
    line-height: 24px;
    width: 232px;
    height: 44px;
    text-transform: none;
    border-radius: 8px;
    padding: 10px 16px;
    color: ${({ theme }) => theme.colors.secondary.main};
    margin-top: 24px;
    box-shadow: none;
    align-self: center;

    &:hover {
        box-shadow: none;
        background: ${({ theme }) => theme.colors.primary.hover};
    }
`;

interface Props {
    onClose: VoidFunction;
    open: boolean;
    avatarType: 'Premium+' | 'Express';
}

const typeToFromPrefix = {
    'Premium+': 'more-express-premiump',
    Express: 'more-express-advanced',
};

export default function LetsTalkModal({ open, onClose, avatarType }: Props) {
    const mixpanel = useAnalytics();

    useEffect(() => {
        if (open) {
            mixpanel?.track('upgrade-package', {
                event: MixPanelEvent.VIEW,
                from: `${typeToFromPrefix[avatarType]}-talk-to-sales`,
            });
        }
    }, [open, avatarType]);

    const onContactUsClick = () => {
        mixpanel?.track('upgrade-package', {
            event: MixPanelEvent.CLICK,
            from: `${typeToFromPrefix[avatarType]}-talk-to-sales`,
        });
    };

    return (
        <BootstrapDialog
            open
            onClose={onClose}
            rootstyle={{
                padding: '40px',
                justifyContent: 'center',
            }}>
            <CloseButton ariaLabel="close" onClick={onClose}>
                <MaskedIcon src={CloseIcon} size="38px" />
            </CloseButton>
            <StyledDialogTitle>Ready to create more {avatarType} Avatars?</StyledDialogTitle>
            <StyledDialogContent>
                <Subtitle>Let's talk. Contact us to explore your options.</Subtitle>
                <StyledButton variant="contained" color="primary" id="avatar-upgrade-modal-contact-us" onClick={onContactUsClick}>
                    Contact us
                </StyledButton>
            </StyledDialogContent>
        </BootstrapDialog>
    );
}
